<template>
    <div :class="getPageContainerClasses">
        <form id="customer-login" method="POST" :action="getFormAction" @submit="onSubmit">
            <input type="hidden" name="_token" :value="csrfToken">
            
            <div id="partner-login">

                <otp-email-form />

                <editable-btn
                    button-type="submit"
                    :prevent-double-click="5000"
                    size="huge"
                    :class="getButtonClasses"
                    :type="getButtonType"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_login"
                >
                    {{ portalContentAndDesignStore.translationSettings.portal_translation_login_login }}
                </editable-btn>

            </div>
        </form>
    </div>

    <!-- Register section outside the container -->
    <div v-if="!portalContentAndDesignStore.portalLayoutSettings?.portal_page_disable_registration_via_portal" class="text-sm text-center" :class="getPageTextColorClass">
        <editable-text-field
            v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_not_registered"
            text-type="span"
        >
        </editable-text-field>
        {{ ' ' }}
        <editable-text-field
            v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_create_account"
            text-type="a"
            :href="portalContentAndDesignStore.isPreview ? '#' : portalContentAndDesignStore.getRegisterRoute"
            :link-target="portalContentAndDesignStore.isExternalRegisterRoute ? '_blank' : '_self'"
            class="hover:underline"
            :class="getPageLinkColorClass"
        >
        </editable-text-field>
    </div>
</template>

<script setup>
import {computed, onMounted, nextTick, watch} from "vue";
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import OtpEmailForm from "../../../../Pub/Pages/Login/Partial/ReferralPortal/OtpEmailForm.vue";
// editable-btn, editable-text-field and editable-social-auth-btn are registered globally
const portalContentAndDesignStore = usePortalContentAndDesignStore();

// Auto-submit the email step when requested via URL (?email=..&autosubmit=1).
// Runs once; guarded so it only fires with a prefilled email and never in preview.
let hasAutoSubmitted = false;
const tryAutoSubmit = () => {
    if (hasAutoSubmitted || !portalContentAndDesignStore.loginAutoSubmit) return;
    if (portalContentAndDesignStore.isPreview) return;
    if (!portalContentAndDesignStore.loginFormData.email) return;

    hasAutoSubmitted = true;
    nextTick(() => {
        const form = document.getElementById('customer-login');
        if (!form) return;
        // requestSubmit runs the @submit validation handler; fall back to submit().
        if (form.requestSubmit) {
            form.requestSubmit();
        } else {
            form.submit();
        }
    });
};

// Defer the initial attempt so the store (email/autosubmit set by the initializer
// in its own onMounted) and the bound input value are ready before we submit.
onMounted(() => nextTick(() => tryAutoSubmit()));
watch(
    () => [portalContentAndDesignStore.loginAutoSubmit, portalContentAndDesignStore.loginFormData.email],
    () => tryAutoSubmit()
);

const csrfToken = computed(() => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
});

const getFormAction = computed(() => {
    // For referral portals, use the standard OTP ini route
    return '/otp/ini';
});

// Referral portal specific container classes
const getPageContainerClasses = computed(() => {
    const style = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_style || 'border';
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    const backgroundColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_background_color || 'white';
    const borderColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_border_color || 'gray-200';
    
    const classes = ['px-6 lg:px-12 py-8 lg:py-14'];
    
    // Background color
    if (backgroundColor.startsWith('#')) {
        classes.push('bg-content-custom-bg');
    } else {
        classes.push(`bg-${backgroundColor}`);
    }
    
    // Layout style
    if (style === 'border') {
        if (borderColor.startsWith('#')) {
            classes.push('border border-content-custom-color');
        } else {
            classes.push(`border border-${borderColor}`);
        }
    } else if (style === 'shadow') {
        classes.push('shadow-lg');
    }
    
    // Corners
    if (corners === 'none') {
        classes.push('rounded-sm');
    } else {
        classes.push(corners);
    }
    
    return classes.join(' ');
});

// Referral portal specific text color class
const getPageContentTextColorClass = computed(() => {
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
    
    if (textColor.startsWith('#')) {
        return 'text-content-custom-text';
    } else {
        return `text-${textColor}`;
    }
});

// Referral portal specific link color class
const getPageContentLinkColorClass = computed(() => {
    const linkColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_link_color || 'teal-600';
    
    if (linkColor.startsWith('#')) {
        return 'text-content-custom-link';
    } else {
        return `text-${linkColor}`;
    }
});

// Button styling based on referral portal settings
const getButtonClasses = computed(() => {
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    
    const classes = ['w-full'];
    
    // Corners
    if (corners === 'none') {
        classes.push('rounded');
    } else {
        classes.push(corners);
    }
    
    return classes.join(' ');
});

const getButtonType = computed(() => {
    const buttonColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_button_color || 'teal';
    
    if (buttonColor.startsWith('#')) {
        return 'custom-content';
    } else {
        return buttonColor;
    }
});

// Page-level text and link colors (for elements outside the content container)
const getPageTextColorClass = computed(() => {
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_text_color || 'gray-800';
    
    if (textColor.startsWith('#')) {
        return 'text-custom-text';
    } else {
        return `text-${textColor}`;
    }
});

const getPageLinkColorClass = computed(() => {
    const linkColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_link_color || 'teal-600';
    
    if (linkColor.startsWith('#')) {
        return 'text-custom-link';
    } else {
        return `text-${linkColor}`;
    }
});

const onSubmit = (event) => {
    // For referral portals, let the form submit normally to the backend
    // The backend will handle the OTP flow and redirect accordingly
    if (portalContentAndDesignStore.isPreview) {
        event.preventDefault();
        return;
    }

    // Use the store as the source of truth (the input is bound via v-model and may
    // be readonly when prefilled); fall back to the DOM value for manual entry.
    const email = portalContentAndDesignStore.loginFormData.email || document.getElementById('email')?.value;

    if (!email) {
        event.preventDefault();
        alert('Please enter your email address');
        return;
    }

    // Allow normal form submission
};
</script>
